import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const UPDATED = '23 September 2026';

const Section = ({ id, title, children }) => (
  <section id={id} className="mt-10">
    <h2 className="text-lg font-bold text-slate-900">{title}</h2>
    <div className="mt-3 space-y-3 text-base text-slate-700 leading-relaxed">{children}</div>
  </section>
);

const TermsPage = () => (
  <div className="min-h-screen bg-white" data-testid="terms-page">
    <SEO
      title="Terms of Service | Paisley.org.uk"
      description="The terms for using Paisley.org.uk: acceptable use, event and property listings, Incident Watch subscriptions, copyright and liability."
      url="/terms"
    />
    <Header />
    <main className="max-w-3xl mx-auto px-6 py-16 md:py-24">
      <p className="text-xs uppercase tracking-[0.3em] font-bold text-cyan-700 mb-3">Legal</p>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">
        Terms of Service
      </h1>
      <p className="mt-4 text-sm text-slate-500">Last updated {UPDATED}</p>

      <p className="mt-8 text-base text-slate-700 leading-relaxed">
        These terms cover your use of Paisley.org.uk, operated from 14 High Street, Paisley,
        Renfrewshire PA1 2BS. By using the site you accept them. They are written to be read, not to
        catch you out.
      </p>

      <Section id="what-we-are" title="What this site is">
        <p>
          Paisley.org.uk is an independent community news and information site. We are not
          Renfrewshire Council, the police, an emergency service or an estate agent, and we are not
          speaking for any of them.
        </p>
      </Section>

      <Section id="accuracy" title="Accuracy, and its limits">
        <p>
          We take care with what we publish and correct mistakes promptly when they are pointed out.
          Even so, information ages: event times change, roadworks finish early, prices move. Always
          check anything that matters with the organiser, agent or authority responsible before you
          rely on it, travel for it or spend money on it.
        </p>
        <p>
          Travel, roadworks and incident information is gathered from third-party feeds including
          Traffic Scotland. It can be delayed, incomplete or wrong. <strong>Never use this site to
          make safety-critical decisions, and never treat it as a substitute for calling 999.</strong>
        </p>
      </Section>

      <Section id="acceptable-use" title="Using the site properly">
        <p>You agree not to:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>submit anything unlawful, defamatory, hateful, misleading or that infringes someone else&apos;s rights;</li>
          <li>submit events, enquiries or messages you have no genuine connection to, or use the forms for marketing;</li>
          <li>scrape, copy or republish our content in bulk, or try to get around our rate limits and security;</li>
          <li>attempt to access the admin area or any account that is not yours.</li>
        </ul>
        <p>
          We can remove content and block access where these terms are broken. Automated scanning of
          the site is blocked and repeated attempts get the IP address banned.
        </p>
      </Section>

      <Section id="submissions" title="Events and other things you send us">
        <p>
          Anyone can submit an event. Submissions are checked before they appear and we may edit,
          shorten or decline them. By submitting, you confirm you have the right to share the
          details and images, and you give us permission to publish them on the site, in our
          newsletter and on our social channels. You keep ownership of what you send.
        </p>
      </Section>

      <Section id="property" title="Property listings and enquiries">
        <p>
          Property listings come from estate agents&apos; own feeds and from OnTheMarket. Prices,
          availability and details are the agent&apos;s, not ours, and can be out of date by the time
          you see them. When you send an enquiry we pass it to the agent marketing that property; any
          viewing, offer or sale is strictly between you and them. We are not a party to the
          transaction, take no fee from buyers and give no advice on the property.
        </p>
      </Section>

      <Section id="incident-watch" title="Incident Watch subscriptions">
        <p>
          Incident Watch is a paid subscription giving access to the full incident feed, email alerts
          and a weekly summary. Payment is taken by Stripe on the plan you choose, monthly or
          annually, and renews automatically until you cancel. You can cancel at any time from the
          billing portal; access continues to the end of the period you have paid for and we do not
          charge a cancellation fee.
        </p>
        <p>
          New subscribers get a free trial where offered, and we email you before the trial turns
          into a charge. Prices are in pounds sterling and include any VAT that applies. If you buy
          as a consumer you have the usual 14-day right to cancel, but by asking for immediate access
          to alerts you agree the service starts straight away, so we may retain a proportionate
          amount for what you have used.
        </p>
        <p>
          We work hard to keep alerts timely but cannot guarantee that every incident is detected,
          that an alert always arrives, or that it arrives first. The service depends on third-party
          feeds and email delivery outside our control.
        </p>
      </Section>

      <Section id="sponsorship" title="Advertising, sponsorship and affiliate links">
        <p>
          Some content is sponsored or contains affiliate links, and is marked as such. Sponsorship
          does not buy editorial coverage or favourable treatment. Advertisers are responsible for
          their own claims; we are not liable for goods or services you buy from them.
        </p>
      </Section>

      <Section id="copyright" title="Copyright">
        <p>
          The articles, photographs and design of this site belong to Paisley.org.uk or are used with
          permission. You are welcome to quote a short extract with a link back to the original. You
          may not republish whole articles, photographs or the archive without asking first — email{' '}
          <a className="text-cyan-700 underline" href="mailto:brian@paisley.org.uk">brian@paisley.org.uk</a>.
        </p>
        <p>
          Some images on the site are generated with AI tools and are illustrative rather than
          photographs of a real moment. If you believe we have used your work without permission,
          tell us and we will remove it or credit it, as you prefer.
        </p>
      </Section>

      <Section id="links" title="Links to other sites">
        <p>
          We link out to organisers, agents, council pages and partners across our own network. We do
          not control those sites and are not responsible for their content, availability or privacy
          practices.
        </p>
      </Section>

      <Section id="liability" title="Liability">
        <p>
          The site is provided as it is. To the extent the law allows, we are not liable for indirect
          or consequential loss, lost profit, or loss arising from your reliance on information here
          or from the site being unavailable. Nothing in these terms limits liability for death or
          personal injury caused by negligence, for fraud, or for anything else that cannot lawfully
          be limited — including your statutory rights as a consumer.
        </p>
      </Section>

      <Section id="general" title="Changes and governing law">
        <p>
          We may update these terms; the date at the top shows when they last changed, and continuing
          to use the site means accepting the current version. Subscribers are told by email about
          changes that affect their subscription. These terms are governed by Scots law and the
          Scottish courts have jurisdiction.
        </p>
      </Section>

      <p className="mt-12 text-sm text-slate-500">
        See also our <Link to="/privacy" className="text-cyan-700 underline">Privacy Policy</Link>, or{' '}
        <Link to="/contact" className="text-cyan-700 underline">get in touch</Link>.
      </p>
    </main>
    <Footer />
  </div>
);

export default TermsPage;
